import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";

/*
 * Admin Home — high-level snapshot of the app.
 *
 * Pulls `/api/admin/summary` on mount and renders four stat groups
 * (signups, redemptions, beta codes, feedback) plus a recent-activity
 * feed of the last 10 audit entries. Every card has a shortcut Link to
 * the detail page it summarises.
 */

function fmtRel(v) {
  if (!v) return "—";
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return String(v);
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return Math.floor(diff / 60_000) + "m ago";
  if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + "h ago";
  return Math.floor(diff / 86_400_000) + "d ago";
}

export default function AdminHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/summary");
      setData(r.data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-home">
      <AdminNav title="Dashboard" />

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
            {loading ? "Loading…" : data?.generated_at
              ? `Snapshot · ${new Date(data.generated_at).toLocaleString()}`
              : "Snapshot"}
          </div>
          <button
            type="button"
            onClick={refresh}
            className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="admin-home-refresh"
          >
            Refresh
          </button>
        </div>

        {/* Primary row: signups / redemptions / sessions / prep-failures / feedback */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            testId="stat-signups-today"
            title="Signups today"
            value={data?.signups?.today ?? "—"}
            hint={
              data
                ? `${data.signups.total} total · ${data.signups.suspended} suspended`
                : ""
            }
            linkTo="/admin/users"
            linkLabel="Manage users →"
            accent="brand"
          />
          <StatCard
            testId="stat-redemptions-today"
            title="Beta redemptions today"
            value={data?.beta_redemptions?.today ?? "—"}
            hint={
              data
                ? `${data.beta_codes.active} active codes · ${data.beta_codes.used} used`
                : ""
            }
            linkTo="/admin/beta-codes"
            linkLabel="Manage codes →"
            accent="emerald"
          />
          <StatCard
            testId="stat-sessions-today"
            title="Interviews today"
            value={data?.engagement?.sessions_today ?? "—"}
            hint={
              data
                ? `${data.engagement.sessions_total} total · ${data.engagement.active_last_7d} weekly active`
                : ""
            }
            accent="sky"
          />
          <StatCard
            testId="stat-prep-failures"
            title="Prep failures (24h)"
            value={data?.prep_failures?.total_last_24h ?? "—"}
            hint={
              data?.prep_failures
                ? `${data.prep_failures.hard_last_24h} hard · ${data.prep_failures.limited_validation_last_24h} limited`
                : ""
            }
            linkTo="/admin/prep-failures"
            linkLabel="Investigate →"
            accent={(data?.prep_failures?.hard_last_24h ?? 0) > 0 ? "rose" : "amber"}
            muted={!data || (data.prep_failures?.total_last_24h ?? 0) === 0}
          />
          <StatCard
            testId="stat-feedback-open"
            title="Open feedback"
            value={data?.feedback?.wired ? (data?.feedback?.open ?? "—") : "—"}
            hint={data?.feedback?.wired
              ? `${data.feedback.today} received today`
              : "Feedback capture not yet wired"}
            accent="amber"
            muted={!data?.feedback?.wired}
          />
        </div>

        {/* Beta code breakdown */}
        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
                Beta code inventory
              </div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                Live counts by status
              </div>
            </div>
            <Link
              to="/admin/beta-codes"
              className="text-xs text-[color:var(--brand)] hover:underline"
              data-testid="beta-inventory-link"
            >
              Manage codes →
            </Link>
            <Link
              to="/admin/beta-reports"
              className="text-xs text-[color:var(--brand)] hover:underline ml-4"
              data-testid="beta-reports-link"
            >
              Beta Reports →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Active" value={data?.beta_codes?.active ?? "—"} color="emerald" testId="beta-count-active" />
            <MiniStat label="Used" value={data?.beta_codes?.used ?? "—"} color="slate" testId="beta-count-used" />
            <MiniStat label="Disabled" value={data?.beta_codes?.disabled ?? "—"} color="rose" testId="beta-count-disabled" />
            <MiniStat label="Expired" value={data?.beta_codes?.expired ?? "—"} color="amber" testId="beta-count-expired" />
          </div>
        </section>

        <AiCosts />

        {/* Recent admin activity */}
        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
                Recent admin activity
              </div>
              <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                Last 10 mutations across the panel
              </div>
            </div>
            <Link
              to="/admin/audit"
              className="text-xs text-[color:var(--brand)] hover:underline"
              data-testid="audit-link"
            >
              Full audit log →
            </Link>
          </div>
          {loading ? (
            <div className="text-sm text-[color:var(--text-secondary)]">Loading…</div>
          ) : (data?.recent_admin_actions?.length ?? 0) === 0 ? (
            <div className="text-sm text-[color:var(--text-secondary)]" data-testid="recent-activity-empty">
              No admin actions yet. Grant Beta access, create codes, or adjust credits and they'll show up here.
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--border-stone)]" data-testid="recent-activity-list">
              {data.recent_admin_actions.map((e) => (
                <li key={e.entry_id} className="py-2 flex items-center gap-3 text-xs">
                  <span className="inline-block min-w-[7rem] rounded-full border border-[color:var(--border-stone)] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[color:var(--text-primary)]">
                    {e.action}
                  </span>
                  <span className="font-mono break-all flex-1">{e.target_id}</span>
                  <span className="text-[color:var(--text-secondary)] whitespace-nowrap">{fmtRel(e.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

// AI cost per completed interview (backend ai_usage.py, 2026-10-28), for pricing.
// Real and simulated interviews are shown apart; only interviews completed since
// tracking began have usage.
function AiCosts() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/ai-costs?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((ex) => toast.error(formatApiErrorDetail(ex)));
    return () => { cancelled = true; };
  }, [days]);
  const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(3)}`);
  const group = (label, g, testId) => (
    <div className="rounded-xl border border-[color:var(--border-stone)] p-4" data-testid={testId}>
      <div className="text-xs font-semibold">{label}</div>
      {!g || !g.interviews ? (
        <div className="mt-2 text-xs text-[color:var(--text-secondary)]">No completed interviews with usage yet.</div>
      ) : (
        <>
          <div className="mt-2 text-2xl font-semibold">{usd(g.average_usd)}</div>
          <div className="text-xs text-[color:var(--text-secondary)]">
            average per interview · {g.interviews} interviews · highest {usd(g.max_usd)} · ~{Math.round(g.average_tokens / 1000)}k tokens
          </div>
          <ul className="mt-3 space-y-1 text-xs">
            {Object.entries(g.by_step_usd || {}).slice(0, 8).map(([step, v]) => (
              <li key={step} className="flex justify-between"><span className="font-mono">{step}</span><span>{usd(v)}</span></li>
            ))}
          </ul>
          {(g.unpriced || []).length > 0 && (
            <div className="mt-2 text-[11px] text-amber-700">Not priced: {g.unpriced.join(", ")}</div>
          )}
        </>
      )}
    </div>
  );
  return (
    <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5" data-testid="admin-ai-costs">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">AI cost per interview</div>
          <div className="text-xs text-[color:var(--text-secondary)] mt-0.5">
            Chat-model tokens at OpenAI list prices; speech-to-text and voice are counted but not priced.
          </div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period"
          className="rounded border border-[color:var(--border-stone)] px-1 text-xs" data-testid="admin-ai-costs-days">
          {[7, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {group("Real interviews", data?.real, "admin-ai-costs-real")}
        {group("Simulated (test) interviews", data?.simulated, "admin-ai-costs-simulated")}
      </div>
    </section>
  );
}

const ACCENT_STYLES = {
  brand:   "border-[color:var(--brand)]/30 bg-[color:var(--brand)]/5",
  emerald: "border-emerald-200 bg-emerald-50",
  sky:     "border-sky-200 bg-sky-50",
  amber:   "border-amber-200 bg-amber-50",
  rose:    "border-rose-200 bg-rose-50",
};

function StatCard({ title, value, hint, testId, linkTo, linkLabel, accent, muted }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${muted ? "border-dashed border-[color:var(--border-stone)] bg-white" : ACCENT_STYLES[accent] || "border-[color:var(--border-stone)] bg-white"}`}
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
        {title}
      </div>
      <div className={`mt-2 text-3xl font-semibold tracking-tight ${muted ? "text-[color:var(--text-secondary)]" : "text-[color:var(--text-primary)]"}`}>
        {value}
      </div>
      {hint && (
        <div className="mt-1 text-[11px] text-[color:var(--text-secondary)]">{hint}</div>
      )}
      {linkTo && (
        <Link
          to={linkTo}
          className="mt-3 inline-block text-[11px] font-medium text-[color:var(--brand)] hover:underline"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

const MINI_COLORS = {
  emerald: "text-emerald-700",
  slate:   "text-slate-700",
  rose:    "text-rose-700",
  amber:   "text-amber-800",
};

function MiniStat({ label, value, color, testId }) {
  return (
    <div className="rounded-xl border border-[color:var(--border-stone)] p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
        {label}
      </div>
      <div className={`mt-0.5 text-2xl font-semibold tracking-tight ${MINI_COLORS[color] || ""}`}>
        {value}
      </div>
    </div>
  );
}
